import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Mail, ScrollText, Sparkles, Users, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import NexusFace from './NexusFace';
import { AGENT_BETA_TERMS_VERSION } from '../lib/agentBetaConstants';

/**
 * First-run gate for Agents.
 * Flow: intro (what the team does) → beta and pricing notice → Agents terms.
 */

type Step = 'intro' | 'beta' | 'terms';

const LIVE_LINES = [
  'Following up with a lead',
  'Drafting an email',
  'Checking the calendar',
  'Working in the background',
];

interface AgentBetaIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the terms row is saved. */
  onAccepted: () => void;
}

export default function AgentBetaIntroModal({ isOpen, onClose, onAccepted }: AgentBetaIntroModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('intro');
  const [isSaving, setIsSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [lineIndex, setLineIndex] = useState(0);
  const [seenOpen, setSeenOpen] = useState(isOpen);

  if (isOpen !== seenOpen) {
    setSeenOpen(isOpen);
    if (isOpen) {
      setStep('intro');
      setAgreed(false);
      setLineIndex(0);
    }
  }

  useEffect(() => {
    if (!isOpen || step !== 'intro') return;
    const timer = window.setInterval(() => {
      setLineIndex((current) => (current + 1) % LIVE_LINES.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [isOpen, step]);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!supabase || !user?.id || isSaving || !agreed) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('user_agent_beta_acceptance')
        .upsert(
          {
            user_id: user.id,
            version: AGENT_BETA_TERMS_VERSION,
            accepted_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );
      if (error) throw error;
      onAccepted();
    } catch (err) {
      console.error('Error saving Agents beta acceptance:', err);
      toast.error('Could not save your acceptance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const title =
    step === 'intro' ? 'Meet your AI team'
      : step === 'beta' ? 'Joining the beta'
        : 'Agents terms';

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center"
      style={{
        paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(12px, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(12px, env(safe-area-inset-right, 0px))',
      }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <style>{`
        @keyframes agent-rise {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes agent-pop {
          from { opacity: 0; transform: scale(0.86); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes agent-ring {
          0% { opacity: 0.45; transform: scale(0.72); }
          100% { opacity: 0; transform: scale(1.35); }
        }
        @keyframes agent-line {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .agent-anim { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { if (!isSaving) onClose(); }} />

      <div className="relative w-full sm:max-w-[420px] bg-white rounded-[28px] shadow-2xl overflow-hidden max-h-full flex flex-col">
        <div className="relative bg-neutral-950 text-white flex-shrink-0 overflow-hidden">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {step === 'intro' ? (
            <div className="px-6 pt-6 pb-4">
              <div className="relative mx-auto h-[136px] w-[240px]">
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <span
                    className="agent-anim block h-28 w-28 rounded-full border border-white/20"
                    style={{ animation: 'agent-ring 2.8s ease-out infinite' }}
                  />
                </span>
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <span className="agent-anim block" style={{ animation: 'agent-pop 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both' }}>
                    <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white shadow-[0_0_40px_rgba(255,255,255,0.18)]">
                      <NexusFace size={64} />
                    </span>
                  </span>
                </span>
                <OrbitChip className="left-0 top-3" delay="0.35s" icon={<Users className="h-3.5 w-3.5" />} label="Leads" />
                <OrbitChip className="right-0 top-3" delay="0.5s" icon={<Mail className="h-3.5 w-3.5" />} label="Email" />
                <OrbitChip className="left-1/2 bottom-0 -translate-x-1/2" delay="0.65s" icon={<Calendar className="h-3.5 w-3.5" />} label="Calendar" />
              </div>
              <p className="mt-1 text-center text-[13px] text-white/70 h-5" key={lineIndex}>
                <span className="agent-anim inline-block" style={{ animation: 'agent-line 0.45s ease both' }}>
                  {LIVE_LINES[lineIndex]}
                </span>
              </p>
            </div>
          ) : (
            <div className="px-6 pt-8 pb-5 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                {step === 'beta' ? <Sparkles className="h-6 w-6" /> : <ScrollText className="h-6 w-6" />}
              </div>
              <h2 className="text-xl font-bold tracking-tight">{title}</h2>
            </div>
          )}

          <div className="flex justify-center gap-1.5 pb-4">
            {(['intro', 'beta', 'terms'] as Step[]).map((item) => (
              <span
                key={item}
                className={`h-1 rounded-full transition-all ${item === step ? 'w-5 bg-white' : 'w-1.5 bg-white/30'}`}
              />
            ))}
          </div>
        </div>

        {step === 'intro' && (
          <>
            <StepBody>
              <h2 className="text-[20px] font-bold tracking-tight text-gray-900">{title}</h2>
              <p className="mt-1 text-sm text-gray-500">
                Nexus and specialist bots that draft, follow up, and keep working after you leave the chat.
              </p>
              <div className="mt-3 space-y-2.5">
                <Feature
                  delay="0.05s"
                  title="One place to ask"
                  body="Tell Nexus what you need. It can work across leads, messages, marketing, bookings, and the rest of your apps."
                />
                <Feature
                  delay="0.16s"
                  title="A team, not a single reply"
                  body="Specialists take pieces of the work. Customer sends wait for you, unless you turn on a routine that runs on its own."
                />
                <Feature
                  delay="0.27s"
                  title="They keep going in the background"
                  body="Bots can work while you are away. That work spends AI tokens from your business balance."
                />
              </div>
              <div
                className="agent-anim mt-3 rounded-2xl bg-neutral-950 px-4 py-3 text-white"
                style={{ animation: 'agent-rise 0.5s ease 0.38s both' }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">Join the beta</p>
                <p className="mt-1 text-sm text-white/90">Be one of the first businesses to run an AI team inside Nexus.</p>
              </div>
            </StepBody>
            <Footer>
              <CancelButton onClick={onClose} disabled={isSaving} />
              <PrimaryButton onClick={() => setStep('beta')}>Join beta</PrimaryButton>
            </Footer>
          </>
        )}

        {step === 'beta' && (
          <>
            <StepBody className="space-y-3">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-950">
                <p className="font-semibold">This is a beta feature.</p>
                <p className="mt-1">
                  Agents are new and still changing. Things can break, behave unexpectedly, or be removed.
                </p>
              </div>
              <p className="text-[13px] leading-relaxed text-gray-600">
                This feature may later become a paid feature. The cost of Agents can change, including any future subscription and the rate at which bots use AI tokens.
              </p>
              <p className="text-[13px] leading-relaxed text-gray-600">
                Joining the beta does not lock in today’s price, a free allowance, or continued access.
              </p>
              <p className="text-[13px] leading-relaxed text-gray-500">
                Next you’ll accept the Agents terms. Those terms are what let you in.
              </p>
            </StepBody>
            <Footer>
              <CancelButton onClick={onClose} disabled={isSaving} />
              <PrimaryButton onClick={() => setStep('terms')}>Continue</PrimaryButton>
            </Footer>
          </>
        )}

        {step === 'terms' && (
          <>
            <StepBody>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-[13px] leading-relaxed text-gray-600 space-y-3 max-h-[42vh] overflow-y-auto">
                <p className="font-semibold text-gray-900">Nexus Agents Beta Terms</p>
                <p>
                  These Agents Beta Terms (“Agents Terms”) govern your use of Nexus Agents, including Nexus, specialist bots, routines, background workers, and any tools they use (“Agents”). By joining the beta you agree to these Agents Terms, in addition to the Nexus Terms of Service and the Nexus AI Services Terms.
                </p>

                <p className="font-semibold text-gray-800 pt-1">1. Beta, and pricing that can change</p>
                <p>
                  Agents are a beta feature. Nexus may change, limit, suspend, or withdraw Agents at any time, with or without notice. Agents may later become a paid feature. The price of Agents, any subscription for Agents, and the rate at which Agents consume AI tokens may change at any time. Joining the beta does not lock in a price, a free allowance, a token rate, or continued access.
                </p>

                <p className="font-semibold text-gray-800 pt-1">2. Autonomous systems</p>
                <p>
                  Agents are autonomous and semi-autonomous software. They can plan, choose tools, browse, draft, and take actions in your business account without you watching each step. Routines and other background workers can keep working while you are away from the app, including sending communications and changing records when you have allowed that. You are responsible for every agent, routine, schedule, and permission you turn on.
                </p>

                <p className="font-semibold text-gray-800 pt-1">3. You are responsible for what Agents do</p>
                <p>
                  Anything an agent does is done for you and is your act. This includes messages, emails, calls, bookings, invoices, proposals, payments, CRM and customer changes, marketing, files, deletions, and any other action the product allows. You must review anything that matters before you rely on it or let it reach a customer, employee, or anyone else. If you approve a send, or you enable a routine that acts without a fresh approval, you are the sender and the actor. Nexus does not supervise your agents for you.
                </p>

                <p className="font-semibold text-gray-800 pt-1">4. No liability</p>
                <p>
                  To the fullest extent permitted by law, Nexus and its affiliates, officers, employees, and suppliers are not liable for any claim, loss, or damage arising out of or related to Agents. This includes direct, indirect, incidental, special, consequential, exemplary, and punitive damages, whether or not Nexus was advised of the possibility. It includes lost profits, lost data, lost customers, token charges, incorrect or duplicate bookings, messages or emails sent to the wrong person, actions you did not expect or did not personally approve in the moment, reliance on agent output, and downtime. You use Agents entirely at your own risk.
                </p>
                <p>
                  If a law does not allow a complete exclusion, Nexus’s total liability for Agents is limited to the amount you paid Nexus specifically for Agents in the three months before the claim, or fifty (50) US dollars if you paid nothing.
                </p>

                <p className="font-semibold text-gray-800 pt-1">5. Token usage</p>
                <p>
                  Agents spend AI tokens from your business balance. Background work, long tasks, retries, web browsing, voice, and routines can spend tokens quickly and while nobody is watching the chat. You are responsible for your balance, for any limits you set, and for turning off agents or routines you do not want running. Nexus is not responsible for token use you consider unexpected, excessive, or unwanted, for charges that follow from that use, or for work that stops when the balance runs out. Unused tokens have no cash value except where the product separately says otherwise.
                </p>

                <p className="font-semibold text-gray-800 pt-1">6. No warranty and no professional advice</p>
                <p>
                  AGENTS ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXUS DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. Output can be wrong, incomplete, offensive, or unsuitable. Agents do not provide legal, financial, tax, accounting, employment, medical, or other professional advice.
                </p>

                <p className="font-semibold text-gray-800 pt-1">7. Your data and third parties</p>
                <p>
                  To run Agents, Nexus may send prompts, business records, customer information, messages, and files to third-party model, hosting, and tool providers. You confirm you have the rights needed to submit that information. Nexus does not control those providers, their availability, or their models. You are responsible for compliance with laws that apply to your business and your customers, including privacy and marketing rules.
                </p>

                <p className="font-semibold text-gray-800 pt-1">8. Acceptable use</p>
                <p>
                  You will not use Agents to break the law, mislead anyone, send spam, harass, infringe rights, or attack systems. Nexus may suspend Agents for abuse, risk, or a breach of these terms.
                </p>

                <p className="font-semibold text-gray-800 pt-1">9. Indemnity</p>
                <p>
                  You will defend, indemnify, and hold harmless Nexus and its affiliates, officers, and employees from claims, damages, losses, and expenses (including reasonable legal fees) arising from your use of Agents, content they create or send for you, actions they take in your account, information you submit, or your breach of these terms or of any law.
                </p>

                <p className="font-semibold text-gray-800 pt-1">10. Changes</p>
                <p>
                  Nexus may update these Agents Terms. If a change is material, Nexus may ask you to accept the new version before you continue. Agents stay closed until you do.
                </p>
              </div>

              <label className="mt-3 flex items-start gap-2.5 text-[13px] leading-snug text-gray-700">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                />
                <span>
                  I understand Agents can act on their own, including in the background, that I am responsible for what they do and for the tokens they use, and that Nexus is not liable for their actions. Pricing for this feature may change.
                </span>
              </label>
            </StepBody>
            <Footer>
              <CancelButton onClick={onClose} disabled={isSaving} />
              <PrimaryButton onClick={handleAccept} disabled={isSaving || !agreed}>
                {isSaving ? 'Saving…' : 'Agree and join'}
              </PrimaryButton>
            </Footer>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function OrbitChip({
  className,
  delay,
  icon,
  label,
}: {
  className: string;
  delay: string;
  icon: ReactNode;
  label: string;
}) {
  return (
    <span className={`absolute ${className}`}>
      <span
        className="agent-anim flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-900 shadow-lg"
        style={{ animation: `agent-rise 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay} both` }}
      >
        {icon}
        {label}
      </span>
    </span>
  );
}

function StepBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.scrollTop = 0;
    const frame = window.requestAnimationFrame(() => { node.scrollTop = 0; });
    return () => window.cancelAnimationFrame(frame);
  }, []);
  return (
    <div
      ref={ref}
      className={`px-6 py-5 overflow-y-auto ${className}`}
      style={{ overflowAnchor: 'none' }}
    >
      {children}
    </div>
  );
}

function Feature({ title, body, delay }: { title: string; body: string; delay: string }) {
  return (
    <div className="agent-anim" style={{ animation: `agent-rise 0.5s ease ${delay} both` }}>
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500">{body}</p>
    </div>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return (
    <div className="px-6 pb-5 pt-1 flex gap-2 flex-shrink-0">
      {children}
    </div>
  );
}

function CancelButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex-1 py-3 rounded-2xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-60"
    >
      Cancel
    </button>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex-[1.4] py-3 rounded-2xl bg-neutral-950 text-white text-sm font-semibold shadow-lg active:scale-[0.98] transition-all disabled:opacity-40"
    >
      {children}
    </button>
  );
}
